import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
    Settings,
    User,
    Palette,
    Heart,
    Bell,
    Save,
    Moon,
    Sun,
    Edit,
    X
} from 'lucide-react';

const SettingsPage = () => {
    const { user, updateUserPreferences, updateProfile } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();
    const { t, formatDate } = useLanguage();

    const [activeSection, setActiveSection] = useState('profile');
    const [isEditing, setIsEditing] = useState(false);
    const [, setIsLoading] = useState(false);
    const [userInfo, setUserInfo] = useState({
        name: user?.name || '',
        email: user?.email || '',
        bio: user?.bio || '',
        birthDate: user?.birth_date || ''
    });
    const [preferences, setPreferences] = useState({
        favoriteGenres: (user?.preferences?.genres && Array.isArray(user.preferences.genres)) ? user.preferences.genres : [],
        notifications: {
            newRecommendations: true,
            weeklyDigest: true,
            trendingMovies: false
        }
    });

    // Kullanıcı değiştiğinde preferences'ı ve userInfo'yu güncelle
    React.useEffect(() => {
        setPreferences(prev => ({
            ...prev,
            favoriteGenres: (user?.profile?.preferences?.genres && Array.isArray(user.profile.preferences.genres))
                ? user.profile.preferences.genres
                : (user?.preferences?.genres && Array.isArray(user.preferences.genres))
                    ? user.preferences.genres
                    : []
        }));
        setUserInfo({
            name: user?.profile?.name || user?.name || '',
            email: user?.profile?.email || user?.email || '',
            bio: user?.profile?.bio || user?.bio || '',
            birthDate: user?.profile?.birth_date || user?.birth_date || ''
        });
    }, [user]);

    const movieGenres = [
        "Aksiyon", "Komedi", "Drama", "Korku", "Romantik", "Bilim Kurgu", "Gerilim", "Suç", "Macera", "Fantastik", "Animasyon", "Aile"
    ];

    const settingsSections = React.useMemo(() => [
        { id: 'profile', label: t('settings.profile'), icon: User },
        { id: 'preferences', label: t('settings.preferences'), icon: Heart },
        { id: 'theme', label: t('settings.theme'), icon: Palette },
        { id: 'notifications', label: t('settings.notifications'), icon: Bell },
    ], [t]);

    const handleSaveProfile = async () => {
        try {
            setIsLoading(true);

            // Supabase profil tablosunu güncelle
            if (updateProfile) {
                await updateProfile({
                    name: userInfo.name,
                    bio: userInfo.bio || null,
                    birth_date: userInfo.birthDate || null
                });
            } else {
                // Fallback: localStorage (eski sistem)
                const updatedUser = { ...user, ...userInfo };
                localStorage.setItem('film_app_user', JSON.stringify(updatedUser));
            }

            setIsEditing(false);
            alert(t('settings.profileSaved'));
        } catch (error) {
            console.error('Profil kaydetme hatası:', error);
            alert(t('settings.profileSaveError') + ': ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePreferences = async () => {
        try {
            setIsLoading(true);

            // Supabase profil tablosunu güncelle
            if (updateUserPreferences) {
                await updateUserPreferences({
                    genres: preferences.favoriteGenres
                });
            } else {
                // Fallback: localStorage
                const updatedUser = {
                    ...user,
                    preferences: {
                        ...user?.preferences,
                        genres: preferences.favoriteGenres
                    }
                };
                localStorage.setItem('film_app_user', JSON.stringify(updatedUser));
            }

            alert(t('settings.preferencesSaved'));
        } catch (error) {
            console.error('Tercih kaydetme hatası:', error);
            alert(t('settings.preferencesSaveError') + ': ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleGenre = (genre) => {
        setPreferences(prev => ({
            ...prev,
            favoriteGenres: prev.favoriteGenres.includes(genre)
                ? prev.favoriteGenres.filter(g => g !== genre)
                : [...prev.favoriteGenres, genre]
        }));
    };

    const resetPreferences = () => {
        if (window.confirm(t('settings.resetConfirm'))) {
            setPreferences(prev => ({
                ...prev,
                favoriteGenres: []
            }));
            updateUserPreferences({
                ...user?.preferences,
                genres: []
            });
        }
    };

    const renderProfileSection = () => (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('settings.profileInfo')}</h3>
                <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                >
                    {isEditing ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                    <span>{isEditing ? t('common.cancel') : t('common.edit')}</span>
                </button>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('settings.name')}
                    </label>
                    {isEditing ? (
                        <input
                            type="text"
                            value={userInfo.name}
                            onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    ) : (
                        <p className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                            {userInfo.name}
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('settings.email')}
                    </label>
                    <p className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                        {userInfo.email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('settings.emailNotChangeable')}
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('settings.birthDate')}
                    </label>
                    {isEditing ? (
                        <input
                            type="date"
                            value={userInfo.birthDate}
                            onChange={(e) => setUserInfo(prev => ({ ...prev, birthDate: e.target.value }))}
                            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    ) : (
                        <p className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                            {userInfo.birthDate ? formatDate(userInfo.birthDate) : t('settings.notSpecified')}
                        </p>
                    )}
                </div>

                {isEditing && (
                    <button
                        onClick={handleSaveProfile}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                        <Save className="h-4 w-4" />
                        <span>{t('common.save')}</span>
                    </button>
                )}
            </div>
        </div>
    );

    const renderPreferencesSection = () => (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('settings.moviePreferences')}</h3>
                <div className="flex space-x-2">
                    <button
                        onClick={resetPreferences}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                        <X className="h-4 w-4" />
                        <span>{t('settings.reset')}</span>
                    </button>
                    <button
                        onClick={handleSavePreferences}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                    >
                        <Save className="h-4 w-4" />
                        <span>{t('common.save')}</span>
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    {t('settings.favoriteGenres')} ({preferences.favoriteGenres?.length || 0} {t('settings.selected')})
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {movieGenres.map(genre => (
                        <button
                            key={genre}
                            onClick={() => toggleGenre(genre)}
                            className={`px-4 py-2 text-sm rounded-lg border transition-colors ${preferences.favoriteGenres.includes(genre)
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                                }`}
                        >
                            {genre}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderThemeSection = () => (
        <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('settings.themeSettings')}</h3>

            <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                        {isDarkMode ? <Moon className="h-5 w-5 text-purple-600" /> : <Sun className="h-5 w-5 text-yellow-500" />}
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                                {isDarkMode ? t('settings.darkTheme') : t('settings.lightTheme')}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {isDarkMode ? t('settings.darkThemeDesc') : t('settings.lightThemeDesc')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={toggleTheme}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDarkMode ? 'bg-purple-600' : 'bg-gray-300'
                            }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-1'
                                }`}
                        />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderNotificationsSection = () => (
        <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('settings.notificationSettings')}</h3>

            <div className="text-center py-12">
                <Bell className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {t('settings.comingSoon')}
                </h4>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    {t('settings.notificationComingSoonDesc')}
                </p>
            </div>
        </div>
    );


    const renderActiveSection = () => {
        switch (activeSection) {
            case 'profile':
                return renderProfileSection();
            case 'preferences':
                return renderPreferencesSection();
            case 'theme':
                return renderThemeSection();
            case 'notifications':
                return renderNotificationsSection();
            default:
                return renderProfileSection();
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
            <div className="text-center mb-6 sm:mb-8">
                <div className="flex justify-center items-center space-x-2 mb-4">
                    <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('settings.title')}</h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400 px-4">
                    {t('settings.subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Sidebar */}
                <div className="lg:col-span-1">
                    <nav className="space-y-2">
                        {settingsSections.map(section => {
                            const Icon = section.icon;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${activeSection === section.id
                                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span className="font-medium">{section.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Content */}
                <div className="lg:col-span-3">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        {renderActiveSection()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage; 